import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { ProCard, ProTable } from '@ant-design/pro-components';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Result,
  Space,
  Typography,
  Upload,
} from 'antd';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Empty from '@/components/QuietEmpty';
import type { CrmProColumnCompat } from '@/modules/zy-crm/types/pro-table-compat';
import { formatCrmDate } from '@/modules/zy-crm/utils/display';
import {
  deleteAttachment,
  getAttachmentDownloadUrl,
  getAttachmentPreviewUrl,
  queryAttachments,
  updateAttachment,
  uploadAttachment,
} from '@/services/bff/attachment.api';
import type { AttachmentItem } from '@/services/bff/types/attachment';
import styles from '../lead-detail-drawer/style.module.less';
import type { AttachmentPanelProps } from './types';

const { Text } = Typography;

type AttachmentCreateFormValues = {
  versionNo?: number;
  remark?: string;
};

type AttachmentRenameFormValues = {
  fileName?: string;
};

function EmptyBlock({ description }: { description: string }) {
  return (
    <div className={styles.emptyBlock}>
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
    </div>
  );
}

function buildHint(total: number, noun: string, pageSize = 5) {
  return total > pageSize
    ? `已展示最近 ${pageSize} 条，共 ${total} 条${noun}`
    : `共 ${total} 条${noun}`;
}

export function AttachmentPanel({
  bizType,
  bizUuid,
  createLabel = '上传附件',
  emptyText = '暂无附件',
  title = '附件',
  hint,
  allowRename = true,
  onDataLoaded,
}: AttachmentPanelProps) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<Error>();
  const [items, setItems] = useState<AttachmentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<RcFile | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [editingAttachment, setEditingAttachment] =
    useState<AttachmentItem | null>(null);
  const [form] = Form.useForm<AttachmentCreateFormValues>();
  const [renameForm] = Form.useForm<AttachmentRenameFormValues>();

  const resolvedHint = useMemo(
    () => hint || buildHint(total || 0, title),
    [hint, total, title],
  );

  const loadAttachments = useCallback(async () => {
    if (!bizType || !bizUuid) {
      setItems([]);
      setTotal(0);
      setLoadError(undefined);
      onDataLoaded?.({ list: [], total: 0 });
      return;
    }
    setLoading(true);
    setLoadError(undefined);
    try {
      const resp = await queryAttachments({
        bizType,
        bizUuid,
        page: 1,
        pageSize: 5,
      });
      const nextItems = resp.list || [];
      const nextTotal = resp.total || 0;
      setItems(nextItems);
      setTotal(nextTotal);
      onDataLoaded?.({ list: nextItems, total: nextTotal });
    } catch (error) {
      const nextError =
        error instanceof Error ? error : new Error('附件加载失败');
      setLoadError(nextError);
      setItems([]);
      setTotal(0);
      onDataLoaded?.({ list: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [bizType, bizUuid, onDataLoaded]);

  const resetCreateState = useCallback(() => {
    form.resetFields();
    form.setFieldsValue({ versionNo: 1 });
    setSelectedFile(null);
    setFileList([]);
  }, [form]);

  const openCreateModal = useCallback(() => {
    resetCreateState();
    setOpen(true);
  }, [resetCreateState]);

  const closeCreateModal = useCallback(() => {
    setOpen(false);
    resetCreateState();
  }, [resetCreateState]);

  const canRenameRecord = useCallback(
    (record: AttachmentItem) => allowRename && record.renameSupported !== false,
    [allowRename],
  );

  const openRenameModal = useCallback(
    (record: AttachmentItem) => {
      if (!canRenameRecord(record)) {
        return;
      }
      setEditingAttachment(record);
      renameForm.setFieldsValue({
        fileName: record.originalFileName || record.fileName,
      });
      setRenameOpen(true);
    },
    [canRenameRecord, renameForm],
  );

  const closeRenameModal = useCallback(() => {
    setRenameOpen(false);
    setEditingAttachment(null);
    renameForm.resetFields();
  }, [renameForm]);

  const submitCreate = useCallback(async () => {
    const values = await form.validateFields();
    if (!selectedFile) {
      message.warning('请先选择文件');
      return;
    }
    setSubmitting(true);
    try {
      await uploadAttachment({
        bizType,
        bizUuid,
        versionNo: values.versionNo || 1,
        remark:
          typeof values.remark === 'string'
            ? values.remark.trim() || undefined
            : undefined,
        file: selectedFile,
      });
      message.success('附件上传成功');
      closeCreateModal();
      await loadAttachments();
    } finally {
      setSubmitting(false);
    }
  }, [bizType, bizUuid, closeCreateModal, form, loadAttachments, selectedFile]);

  const handleDelete = useCallback(
    (uuid: string) => {
      Modal.confirm({
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        title: '确认删除附件',
        content: '删除后不可恢复，是否继续？',
        okText: '删除',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: async () => {
          await deleteAttachment(uuid, { bizType, bizUuid });
          message.success('附件已删除');
          await loadAttachments();
        },
      });
    },
    [bizType, bizUuid, loadAttachments],
  );

  const handleDownload = useCallback(
    async (record: AttachmentItem) => {
      const uuid = String(record.uuid || '').trim();
      if (!uuid) {
        message.error('附件标识无效，无法下载');
        return;
      }
      const result = await getAttachmentDownloadUrl(uuid, { bizType, bizUuid });
      const downloadUrl = String(result.url || '').trim();
      if (!downloadUrl) {
        message.error('附件下载地址无效');
        return;
      }
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download =
        record.originalFileName || record.fileName || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    },
    [bizType, bizUuid],
  );

  const handlePreview = useCallback(
    async (record: AttachmentItem) => {
      const uuid = String(record.uuid || '').trim();
      if (!uuid) {
        message.error('附件标识无效，无法预览');
        return;
      }
      const result = await getAttachmentPreviewUrl(uuid, { bizType, bizUuid });
      const previewUrl = String(result.url || '').trim();
      if (!previewUrl) {
        message.error('附件预览地址无效');
        return;
      }
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    },
    [bizType, bizUuid],
  );

  const submitRename = useCallback(async () => {
    const values = await renameForm.validateFields();
    if (!editingAttachment?.uuid) {
      return;
    }
    setRenaming(true);
    try {
      await updateAttachment(
        String(editingAttachment.uuid),
        {
          fileName: String(values.fileName || '').trim(),
        },
        { bizType, bizUuid },
      );
      message.success('文件名已更新');
      closeRenameModal();
      await loadAttachments();
    } finally {
      setRenaming(false);
    }
  }, [
    bizType,
    bizUuid,
    closeRenameModal,
    editingAttachment,
    loadAttachments,
    renameForm,
  ]);

  const columns = useMemo<CrmProColumnCompat<AttachmentItem>[]>(
    () => [
      {
        title: '文件名',
        dataIndex: 'fileName',
        width: 260,
        fixed: 'left',
        ellipsis: true,
        render: (_, record) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              minWidth: 0,
            }}
          >
            <Text
              ellipsis
              style={{ minWidth: 0, flex: 1 }}
              title={record.originalFileName || record.fileName}
            >
              {record.originalFileName || record.fileName}
            </Text>
            {canRenameRecord(record) ? (
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                aria-label="编辑文件名"
                style={{
                  flex: 'none',
                  width: 22,
                  minWidth: 22,
                  padding: 0,
                  color: 'var(--ant-color-primary)',
                }}
                onClick={() => openRenameModal(record)}
              />
            ) : null}
          </div>
        ),
      },
      {
        title: '类型',
        dataIndex: 'fileExt',
        width: 110,
        renderText: (_, record) => record.fileExt || record.mimeType || '文件',
      },
      {
        title: '版本',
        dataIndex: 'versionNo',
        width: 90,
        align: 'center',
        renderText: (value) => `V${value || 1}`,
      },
      {
        title: '大小',
        dataIndex: 'sizeBytes',
        width: 130,
        align: 'right',
        renderText: (value) => `${Number(value || 0).toLocaleString()} B`,
      },
      {
        title: '备注',
        dataIndex: 'remark',
        width: 220,
        ellipsis: true,
        renderText: (value) => value || '-',
      },
      {
        title: '上传时间',
        dataIndex: 'createdAt',
        width: 180,
        renderText: (value) => formatCrmDate(value) || '-',
      },
      {
        title: '操作',
        key: 'option',
        valueType: 'option',
        width: 180,
        fixed: 'right',
        render: (_, record) => [
          <a key="preview" onClick={() => void handlePreview(record)}>
            预览
          </a>,
          <a key="download" onClick={() => void handleDownload(record)}>
            下载
          </a>,
          <a key="delete" onClick={() => handleDelete(String(record.uuid))}>
            删除
          </a>,
        ],
      },
    ],
    [
      canRenameRecord,
      handleDelete,
      handleDownload,
      handlePreview,
      openRenameModal,
    ],
  );

  useEffect(() => {
    void loadAttachments();
  }, [loadAttachments]);

  return (
    <>
      <ProCard
        title={title}
        extra={
          <Space size={8}>
            <Text type="secondary">{resolvedHint}</Text>
            <Button type="link" size="small" onClick={openCreateModal}>
              {createLabel}
            </Button>
          </Space>
        }
        className={styles.sectionCard}
      >
        {loadError ? (
          <Result
            status="error"
            title="附件加载失败"
            subTitle={loadError.message}
            extra={
              <Button type="primary" onClick={() => void loadAttachments()}>
                重试
              </Button>
            }
          />
        ) : loading ? (
          <Empty description="附件加载中" />
        ) : items.length ? (
          <ProTable<AttachmentItem>
            rowKey="uuid"
            columns={columns}
            dataSource={items}
            size="small"
            search={false}
            options={false}
            pagination={false}
            cardBordered={false}
            tableAlertRender={false}
            tableAlertOptionRender={false}
            toolBarRender={false}
            scroll={{ x: 1170 }}
          />
        ) : (
          <EmptyBlock description={emptyText} />
        )}
      </ProCard>

      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        open={open}
        title="上传附件"
        onCancel={closeCreateModal}
        onOk={() => void submitCreate()}
        confirmLoading={submitting}
        destroyOnHidden
        forceRender
      >
        <Form form={form} layout="vertical" initialValues={{ versionNo: 1 }}>
          <Form.Item name="versionNo" label="版本号">
            <InputNumber min={1} precision={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} maxLength={300} />
          </Form.Item>
          <Form.Item label="文件" required>
            <Upload
              beforeUpload={(file) => {
                const nextFile = file as RcFile;
                setSelectedFile(nextFile);
                setFileList([
                  {
                    uid: nextFile.uid,
                    name: nextFile.name,
                    status: 'done',
                    size: nextFile.size,
                    type: nextFile.type,
                    originFileObj: nextFile,
                  },
                ]);
                return false;
              }}
              onRemove={() => {
                setSelectedFile(null);
                setFileList([]);
              }}
              fileList={fileList}
              maxCount={1}
            >
              <Button icon={<PlusOutlined />}>选择文件</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        open={renameOpen}
        title="修改文件名"
        onCancel={closeRenameModal}
        onOk={() => void submitRename()}
        confirmLoading={renaming}
        destroyOnHidden
        forceRender
      >
        <Form form={renameForm} layout="vertical">
          <Form.Item
            name="fileName"
            label="文件名"
            rules={[
              { required: true, message: '请输入文件名' },
              { max: 255, message: '文件名不能超过 255 个字符' },
            ]}
          >
            <Input placeholder="请输入文件名" maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
